// =====================================================================
// App.jsx - Router principal (modèle Odoo : launcher + barre supérieure)
// =====================================================================
// Day 11 (27-05-2026):
//   - Suppression de la barre latérale gauche partout (modèle Odoo full)
//   - Page d'accueil = launcher en grille (/apps), plein écran
//   - Barre supérieure sur les modules : app-switcher (grille) -> /apps,
//     marque, et menu compte (en haut à droite)
// =====================================================================
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { retourDe } from './lib/retourApresLogin.js';
import { useAuth } from './hooks/useAuth.jsx';
import { estPagePublique } from './lib/pagesPubliques.js';
import Login from './pages/Login.jsx';
import MotDePasseOublie from './pages/MotDePasseOublie.jsx';   // 23-10-2026 — page PUBLIQUE : demander un lien par e-mail
import MotDePasse from './pages/MotDePasse.jsx';               // 23-10-2026 — page PUBLIQUE /mot-de-passe#<jeton>
import Embauche from './pages/Embauche.jsx';   // M-011 (26-08-2026) — page PUBLIQUE /embauche/<token>
import OrderPhotos from './pages/OrderPhotos.jsx';   // Commande prête (13-10-2026) — page PUBLIQUE /order/<token> (client Nespart)
import OrderRdv from './pages/OrderRdv.jsx';             // v3 B (16-10-2026) — page PUBLIQUE /order/<token>/rdv (rendez-vous d'enlèvement)
import OrderDocuments from './pages/OrderDocuments.jsx'; // v3 B — page PUBLIQUE /order/<token>/documents (documents à imprimer)
import CommandePrete from './pages/CommandePrete.jsx';
import CommandePreteEditeur from './pages/CommandePreteEditeur.jsx';
import CommandesPretesChargement from './pages/CommandesPretesChargement.jsx';   // v2 (16-10-2026) — départ du camion
import CommandesPretesClients from './pages/CommandesPretesClients.jsx';         // v2 — paramètres client (admins + coordinateurs)
import ClientPortail from './pages/ClientPortail.jsx';   // v2 — page PUBLIQUE /client/<token> (commandes du client, sans compte)
import AdminUsers from './pages/admin/Users.jsx';
import AdminProjects from './pages/admin/Projects.jsx';
import AdminTasks from './pages/admin/Tasks.jsx';
import AdminPeriodLocks from './pages/admin/PeriodLocks.jsx';
import Conges from './pages/Conges.jsx';
import AdminPermissions from './pages/admin/AdminPermissions.jsx';
import TempsPresence from './pages/TempsPresence.jsx';
import Prelevements from './pages/Prelevements/Prelevements.jsx';
import Impressions from './pages/Impressions/Impressions.jsx';
import Trajets from './pages/Trajets/Trajets.jsx';
import Inventaire from './pages/Inventaire/Inventaire.jsx';
import Palettes from './pages/Palettes/Palettes.jsx';
import Dimensions from './pages/Dimensions.jsx';
import Bordereaux from './pages/Bordereaux.jsx';   // M-015 remise transporteur
import Securite from './pages/Securite.jsx';   // M-010 (25-08-2026)
import DashboardAdmin from './pages/DashboardAdmin.jsx';
import DashboardOperator from './pages/DashboardOperator.jsx';
import ChangePassword from './pages/ChangePassword.jsx';
import CertificatsMedicaux from './pages/CertificatsMedicaux.jsx';
import CahiersDeCharge from './pages/CahiersDeCharge.jsx';          // Day 11 (28-05-2026) — Cahier de charge
import Erreurs from './pages/Erreurs/Erreurs.jsx';                  // M-006 (29-05-2026) — Journal d'erreurs
import Facturation from './pages/Facturation.jsx';                  // M-007 (29-05-2026) — Module Facturation
import Convoyeur from './pages/Convoyeur/Convoyeur.jsx';            // M-008 (24-06-2026) — Convoyeur (tri colis)
import Launcher from './pages/Launcher.jsx';                        // Day 11 — App launcher style Odoo
import AccountMenu from './components/AccountMenu.jsx';
import MobileBottomNav from './components/MobileBottomNav.jsx';   // Day 13+ — Mobile bottom navigation (Combo A+B)
import SafetyAlertBanner from './components/SafetyAlertBanner.jsx';
import Accidents from './pages/Accidents.jsx';

// Landing : launcher (sauf si changement de mot de passe forcé)
function RootRedirect() {
  const { user } = useAuth();
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
  if (user) return <Navigate to="/apps" replace />;
  return <Navigate to="/login" replace />;
}

// 23-10-2026 : juste après la connexion, l'application se redessine connectée alors qu'on est
// encore sur /login — sans cette route, le « * » renvoyait au lanceur et la page demandée avant
// la connexion (ex. /accidents?scan=BS-01, un autocollant scanné) était perdue.
function RetourApresLogin() {
  const { user } = useAuth();
  const location = useLocation();
  const retour = retourDe(location);
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
  return <Navigate to={retour || '/apps'} replace />;
}

// Barre supérieure (modules) : app-switcher + marque + compte
function TopBar() {
  return (
    <header className="odoo-topbar">
      <Link to="/apps" className="odoo-switcher" title="Applications" aria-label="Applications">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="5" r="2" /><circle cx="12" cy="5" r="2" /><circle cx="19" cy="5" r="2" />
          <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
          <circle cx="5" cy="19" r="2" /><circle cx="12" cy="19" r="2" /><circle cx="19" cy="19" r="2" />
        </svg>
      </Link>
      <Link to="/apps" className="odoo-brand">Brain E-Log</Link>
      <div style={{ flex: 1 }} />
      <AccountMenu />
    </header>
  );
}

export default function App() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div style={{ padding: 40 }}>Chargement...</div>;

  // Public routes (M-011: /embauche = candidat SANS compte, via QR ;
  // Commande prête 13-10-2026 : /order = photos du client Nespart, lien du mail ;
  // Commandes prêtes v2 16-10-2026 : /client = page du client, lien sécurisé par client)
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
        <Route path="/mot-de-passe" element={<MotDePasse />} />
        <Route path="/embauche/:token" element={<Embauche />} />
        <Route path="/order/:token" element={<OrderPhotos />} />
        <Route path="/order/:token/rdv" element={<OrderRdv />} />
        <Route path="/order/:token/documents" element={<OrderDocuments />} />
        <Route path="/client/:token" element={<ClientPortail />} />
        {/* 23-10-2026 : la page demandée suit vers /login, pour y revenir après la connexion
            (un autocollant scanné sur un téléphone non connecté ne perd plus son code). */}
        <Route path="*" element={<Navigate to="/login" replace state={{ retour: location.pathname + location.search }} />} />
      </Routes>
    );
  }

  // Pages plein écran (sans barre supérieure) : launcher + changement de mot de passe + embauche (M-011)
  // + photos client de la commande prête + page du client (même page avec ou sans compte Zeus)
  const bare = location.pathname === '/apps' || location.pathname === '/change-password'
    || estPagePublique(location.pathname);   // /embauche/, /order/, /client/, /mot-de-passe… (lib/pagesPubliques.js)

  return (
    <div className="odoo-shell">
      {!bare && <TopBar />}
      {!bare && <SafetyAlertBanner />}
      <main className={bare ? 'odoo-content-full' : 'odoo-content'}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<RetourApresLogin />} />
          <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
          <Route path="/mot-de-passe" element={<MotDePasse />} />
          <Route path="/embauche/:token" element={<Embauche />} />
          <Route path="/order/:token" element={<OrderPhotos />} />
          <Route path="/order/:token/rdv" element={<OrderRdv />} />
          <Route path="/order/:token/documents" element={<OrderDocuments />} />
          <Route path="/client/:token" element={<ClientPortail />} />
          <Route path="/apps" element={<Launcher />} />
          <Route path="/dashboard" element={<DashboardAdmin />} />
          <Route path="/mon-espace" element={<DashboardOperator />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/temps" element={<TempsPresence />} />
          <Route path="/admin/global-timesheets" element={<Navigate to="/temps" replace />} />
          <Route path="/mes-heures" element={<Navigate to="/temps" replace />} />
          <Route path="/admin/presence" element={<Navigate to="/temps" replace />} />
          <Route path="/admin/validation-ts" element={<Navigate to="/temps" replace />} />
          <Route path="/admin/overtime" element={<Navigate to="/temps" replace />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/projects" element={<AdminProjects />} />
          <Route path="/admin/projects/:id/tasks" element={<AdminTasks />} />
          <Route path="/admin/conges" element={<Navigate to="/conges" replace />} />
          <Route path="/admin/period-locks" element={<AdminPeriodLocks />} />
          <Route path="/admin/permissions" element={<AdminPermissions />} />
          <Route path="/certificats-medicaux" element={<CertificatsMedicaux />} />
          <Route path="/cahiers-charge" element={<CahiersDeCharge />} />
          <Route path="/erreurs" element={<Erreurs />} />
          <Route path="/facturation" element={<Facturation />} />
          <Route path="/convoyeur" element={<Convoyeur />} />
          <Route path="/mes-conges" element={<Navigate to="/conges" replace />} />
          <Route path="/conges" element={<Conges />} />
          <Route path="/prelevements" element={<Prelevements />} />
          <Route path="/impressions" element={<Impressions />} />
          <Route path="/admin/printing-catalogs" element={<Navigate to="/impressions" replace />} />
          <Route path="/trajets" element={<Trajets />} />
          <Route path="/mes-courses" element={<Navigate to="/trajets" replace />} />
          <Route path="/inventaire" element={<Inventaire />} />
          <Route path="/palettes" element={<Palettes />} />
          <Route path="/dimensions" element={<Dimensions />} />
          {/* Commandes prêtes (13-10-2026, v2 16-10-2026) : liste + écran PDA d'une commande,
              chargement (départ du camion), paramètres client. Les chemins fixes passent avant /:id. */}
          <Route path="/commande-prete" element={<CommandePrete />} />
          <Route path="/commande-prete/chargement" element={<CommandesPretesChargement />} />
          <Route path="/commande-prete/clients" element={<CommandesPretesClients />} />
          <Route path="/commande-prete/:id" element={<CommandePreteEditeur />} />
          <Route path="/bordereaux" element={<Bordereaux />} />
          <Route path="/securite" element={<Securite />} />
          {/* Les accidents ont leur module : donnees de sante, acces a part. */}
          <Route path="/accidents" element={<Accidents />} />
          <Route path="*" element={<Navigate to="/apps" replace />} />
        </Routes>
      </main>
      {/* Day 13+ — Mobile bottom navigation (vizibil doar <= 640px) */}
      <MobileBottomNav />
    </div>
  );
}
